import { intranet_tools } from "./index";

/**
 * Represents an error response from the HomeSpotter GraphQL API.
 */
class HSApiError extends Error {
    constructor(message, response, json) {
        super(message);
        this.response = response;
        this.json = json;
    }
}

const TOKEN_STORAGE_KEY = "api_token";

/**
 * Returns the current API authorization token, if there is one, or else
 * acquires and returns a new one.
 */
export async function getApiTokenFromSessionStorage() {
    let token = sessionStorage.getItem(TOKEN_STORAGE_KEY);

    if (token !== null) {
        return token;
    }

    return await intranet_tools(`getapitoken`).then(res => {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, res.api_token);

        return res.api_token;
    });
}

/**
 * Sets the API authorization token from input string
 * @param token
 */
export function setAPIToken(token) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
}

/**
 * Clears the current API authorization token.
 */
export function clearApiToken() {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Makes a request to the HomeSpotter GraphQL API.
 *
 * This function automatically acquires an authorization token and attaches it
 * to each request, renewing it as needed.
 *
 * @param query - GraphQL query string
 * @param variables - GraphQL query variables, if any
 * @param headers - Additional request headers (optional)
 * @param retry - If false, will not retry the request after a failure
 * @param debug - If true, will print debug info, such as the query and variables
 * @param api_url - If set, will use api_url instead of env variable
 */
export async function graphql({
    query,
    variables = null,
    headers,
    retry = true,
    debug = false,
    api_url = process.env.API_URL
}) {
    let token = await getApiTokenFromSessionStorage();

    if (debug) {
        console.log(`GraphQL query:\n${formatQuery(query)}\n`);
        console.log("GraphQL variables:", variables);
        console.log("GraphQL URL:", api_url);
    }

    headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
    };
    // ...headers

    const response = await fetch(api_url, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: query, variables: variables })
    });

    if (response.ok) {
        return response.json();
    } else {
        let json;
        try {
            json = await response.json();
        } catch (e) {
            json = null;
        }

        if (retry && shouldRetryRequest(json)) {
            clearApiToken();
            return graphql({ query, variables, headers, retry: false });
        }

        throw new HSApiError(
            `GraphQL query failed with HTTP status ${response.status}`,
            response,
            json
        );
    }
}

/**
 * Returns true if the response indicates an issue with the API token, and the
 * request should be retried after acquiring a new token.
 */
function shouldRetryRequest(responseJson) {
    return responseJson != null
        && responseJson.message != null
        && (responseJson.message.includes("Invalid token")
            || responseJson.message.includes("signature_error"));
}

/**
 * Formats a GraphQL query for display
 */
export function formatQuery(query) {
    const indentStr = "  ";
    let curIndent = 0;

    return query
        .split("\n")
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
            const indents = (line.match(/{/g) || []).length;
            const dedents = (line.match(/}/g) || []).length;
            const thisLineIndent = curIndent - Math.max(0, dedents - indents);
            curIndent = curIndent + indents - dedents;
            return (
                Array(thisLineIndent)
                    .fill(indentStr)
                    .join("") + line
            );
        })
        .join("\n");
}

export default graphql;
